import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApiService, Mk2Request, Pagination } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    EnumInput,
    mapBuyerPayableSortColumn,
    PayablesInput,
    PayableStatus,
    SortDirection
} from '@lsq-frontend/remote-services/financing-graphql';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { EnumType, jsonToGraphQLQuery } from 'json-to-graphql-query';
import { isNil, omitBy } from 'lodash';
import { startWith } from 'rxjs/operators';
import { IExportRequest } from '../models/export-request';
import { ExportType } from '../models/export-type';

interface IGetBuyerCurrentBalanceExportResponse {
    ok: boolean;
    fileString: string;
    error: Error;
}

export class GetBuyerCurrentBalanceExportResponse
    extends Mk2Request
    implements IGetBuyerCurrentBalanceExportResponse
{
    public ok = false;
    public fileString = '';
    public error: Error = null;

    constructor(obj?: Partial<IGetBuyerCurrentBalanceExportResponse>) {
        super();
        this.ok = obj?.ok ?? this.ok;
        this.fileString = obj?.fileString ?? this.fileString;
        this.error = obj?.error ?? this.error;
    }
}

export class GetBuyerCurrentBalanceExportRequest extends Mk2Request {
    public actualBuyerDueDateRange: DateRange2;
    public pagination?: Pagination;
    constructor(initial?: Partial<GetBuyerCurrentBalanceExportRequest>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

export interface DateRange2 {
    startDate?: string;
    endDate?: string;
}
@Injectable({ providedIn: 'root' })
export class GetCurrentBalanceExportService extends ApiService<
    GetBuyerCurrentBalanceExportRequest,
    GetBuyerCurrentBalanceExportResponse
> {
    public requestType = GetBuyerCurrentBalanceExportRequest;
    public responseType = GetBuyerCurrentBalanceExportResponse;
    public endpointName = 'buyer/BuyerBalanceSummary/GetCurrentBalanceExport';
    public serviceName = 'financing';
    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v2/financingExport';
                    this.serviceName = 'reportCenter';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'buyer/BuyerBalanceSummary/GetCurrentBalanceExport';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }
    public requestMapper(
        req: GetBuyerCurrentBalanceExportRequest
    ): IExportRequest | GetBuyerCurrentBalanceExportRequest {
        if (this._isPayLaterEnabled) {
            const statuses = [
                new EnumInput(PayableStatus.APPROVED),
                new EnumInput(PayableStatus.PAY_LATER),
                new EnumInput(PayableStatus.EARLY_PAID),
                new EnumInput(PayableStatus.PAY_LATER_REQUESTED),
                new EnumInput(PayableStatus.EARLY_PAY_REQUESTED),
                new EnumInput(PayableStatus.EARLY_PAID_PAY_LATER),
                new EnumInput(PayableStatus.CLOSED),
                new EnumInput(PayableStatus.EARLY_PAID_CLOSED),
                new EnumInput(PayableStatus.EARLY_PAID_PAY_LATER_CLOSED),
                new EnumInput(PayableStatus.PAY_LATER_CLOSED)
            ];
            const { skip, take, sortColumn, sortDirection } = req.pagination;
            const input: PayablesInput = omitBy(
                {
                    statuses: statuses,
                    hasPositiveBalanceOrFees: true,
                    dueAsOfOrClosed: req.actualBuyerDueDateRange.endDate,
                    pagination: {
                        skip: skip || 0,
                        take: take || 0,
                        sortColumn: new EnumType(mapBuyerPayableSortColumn(sortColumn)),
                        sortDirection: new EnumType(
                            SortDirection[sortDirection] ?? SortDirection.DESCENDING
                        )
                    }
                },
                isNil
            );
            const query = jsonToGraphQLQuery({
                payables: {
                    __args: { input },
                    rows: {
                        id: true,
                        name: true,
                        statusLegacy: true,
                        amount: {
                            cents: true,
                            currencyCode: true
                        },
                        supplierExternalId: true,
                        amountLessSupplierDiscountFee: {
                            cents: true,
                            currencyCode: true
                        },
                        invoiceDateParent: true,
                        earlyPaymentDate: true,
                        adjustedDueDate: true,
                        isPayLater: true,
                        originalDueDate: true,
                        payLaterAdjustedDueDate: true,
                        payLaterOriginalDueDate: true,
                        dueInDays: true,
                        buyerDiscountFeeRate: true,
                        isGroup: true,
                        nameSearchMatchedNested: true,
                        openAmount: {
                            cents: true,
                            currencyCode: true
                        },
                        feesDue: {
                            cents: true,
                            currencyCode: true
                        },
                        relationship: {
                            supplier: {
                                id: true,
                                company: {
                                    name: true
                                }
                            },
                            scfConfig: {
                                isFboManageAllPayments: true,
                                isPayLaterEnabled: true
                            }
                        },
                        approvals: {
                            id: true,
                            amount: {
                                cents: true,
                                currencyCode: true
                            },
                            adjustedAmount: {
                                cents: true,
                                currencyCode: true
                            },
                            openAmount: {
                                cents: true,
                                currencyCode: true
                            },
                            feesDue: {
                                cents: true,
                                currencyCode: true
                            },
                            feesPaid: {
                                cents: true,
                                currencyCode: true
                            },
                            payLaterAdjustedDueDate: true,
                            originalDueDate: true,
                            invoice: {
                                invoiceNumber: true,
                                date: true,
                                amount: {
                                    cents: true,
                                    currencyCode: true
                                }
                            },
                            creditMemoAllocations: {
                                id: true,
                                approvalId: true,
                                allocatedAmount: {
                                    cents: true,
                                    currencyCode: true
                                },
                                creditMemoId: true,
                                creditMemo: {
                                    creditMemoNumber: true,
                                    date: true,
                                    amount: {
                                        cents: true,
                                        currencyCode: true
                                    },
                                    allocatedAmount: {
                                        cents: true,
                                        currencyCode: true
                                    }
                                },
                                allocationType: true
                            }
                        },
                        creditMemos: {
                            id: true,
                            creditMemoNumber: true,
                            date: true,
                            allocatedAmount: {
                                cents: true,
                                currencyCode: true
                            }
                        }
                    }
                }
            });

            return {
                fileType: 'csv',
                exportType: ExportType.BUYER_CURRENT_BALANCE,
                query: `{${query}}`
            };
        }
        return req;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
